import { inject, Injectable } from '@angular/core'

import { sortBy } from 'lodash-es'
import { MenuItem } from 'primeng/api'
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  firstValueFrom,
  map,
  Observable,
  shareReplay,
  skip,
  switchMap,
  takeUntil,
} from 'rxjs'

import {
  Action,
  DataQueryProps,
  Resource,
  Role,
  TI_CUSTOM_ROLE_ID_RANGE,
  UserRole,
} from '@ti/contracts'
import { AccessControl } from '@ti/core/common/services'
import { DataGridColumnActionsFn, DataGridColumnType } from '@ti/core/contracts/data-grid'
import { USER_DIRECTORY_EVENTS } from '@ti/core/contracts/user-directory-events'
import { CopyOutlinedIconComponent } from '@ti/core/icons/copy-outlined'
import { WrenchIconComponent } from '@ti/core/icons/wrench'
import { ListModel } from '@ti/core/models/list.model'
import { ListModelConfig } from '@ti/core/models/types'

import { UserRolesProvider } from '../providers/user-roles.provider'
import { RolesManager, RoleStore } from '../services'

export type RolesListData = Role & {
  usersCount: number | undefined | null
}

@Injectable()
export abstract class BaseRolesListModel extends ListModel<RolesListData> {
  public abstract allowedActions: Action[]
  public abstract allowedResources: Resource[]
  public abstract defaultReturnRoute: string
  protected abstract readonly modelName: string

  public override readonly title = 'roles.header'

  protected readonly accessControl = inject(AccessControl)
  protected readonly rolesManager = inject(RolesManager)
  protected readonly roleStore = inject(RoleStore)
  protected readonly userEvents = inject(USER_DIRECTORY_EVENTS, { optional: true })
  protected readonly userOptionsProvider = inject(UserRolesProvider)

  protected readonly HIDDEN_ROLES?: UserRole[]

  protected readonly triggerUserCountReload$ = new BehaviorSubject<void>(undefined)
  protected readonly assignedUserCounts$: Observable<Array<{ roleId: number; count: number }>> =
    combineLatest([this.roleStore.state$.items, this.triggerUserCountReload$]).pipe(
      debounceTime(128),
      switchMap(([roles]) => {
        if (roles.length) {
          if (this.HIDDEN_ROLES) {
            roles = roles.filter((role) => !this.HIDDEN_ROLES!.includes(role.id))
          }

          const roleIds = roles.map((r) => r.id)
          return this.api.role.listAssignedUsersCount$(roleIds)
        }
        return Promise.resolve([])
      }),
      takeUntil(this.destroy$),
      shareReplay({ bufferSize: 1, refCount: false }),
    )

  protected readonly sourceData$ = combineLatest([
    this.assignedUserCounts$,
    this.roleStore.state$.items,
    this.userOptionsProvider.userRoleLabels$,
  ]).pipe(
    map(([assignedUserCounts, roles, roleLabels]) => {
      const userCountMap = new Map<number, number>(
        assignedUserCounts?.length
          ? assignedUserCounts.map((data) => [data.roleId, data.count])
          : [],
      )

      if (this.HIDDEN_ROLES) {
        roles = roles.filter((role) => !this.HIDDEN_ROLES!.includes(role.id))
      }

      return roles.map((role) => {
        const isCustom = this.rolesManager.isCustom(role.id)
        return {
          ...role,
          name: isCustom ? role.name : roleLabels[role.id as UserRole],
          usersCount: userCountMap.get(role.id) || 0,
          createdAt: isCustom ? role.createdAt : undefined,
        } as RolesListData
      })
    }),
    takeUntil(this.destroy$),
    shareReplay({ bufferSize: 1, refCount: false }),
  )

  protected override async init() {
    await super.init()

    // Subscribe to users manager updates to refresh assigned user counts
    if (this.userEvents) {
      this.userEvents.updated.subscribe(() => this.triggerUserCountReload$.next())
    }

    // Force refresh the list model after the source data is updated
    this.sourceData$.pipe(skip(1), takeUntil(this.destroy$)).subscribe(() => this.refresh())
  }

  protected async loadPage(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    props: DataQueryProps,
  ): Promise<RolesListData[]> {
    let data = await firstValueFrom(this.sourceData$)

    if (props.search) {
      const searchRE = new RegExp(`.*${props.search}.*`, 'gi')
      data = data.filter((item) => {
        return item.name?.match(searchRE)
      })
    }

    if (props.order && props.orderBy) {
      switch (props.orderBy) {
        case 'name':
          data = sortBy(data, (item) => item.name)
          break
        case 'usersCount':
          data = sortBy(data, (item) => item.usersCount)
          break
        case 'createdAt':
          data.sort((a, b) => {
            const dateA = a.createdAt
            const dateB = b.createdAt

            // Exclude roles with undefined date from sorting
            if (dateA === undefined && dateB === undefined) {
              return 0
            }
            if (dateA === undefined) {
              return 1
            }
            if (dateB === undefined) {
              return -1
            }

            const timeA = new Date(dateA).getTime()
            const timeB = new Date(dateB).getTime()

            if (props.order === 'DESC') {
              return timeB - timeA
            } else {
              return timeA - timeB
            }
          })

          // Cancel additional reverse of the list
          props.order = undefined
          break
      }

      if (props.order === 'DESC') {
        data = data.reverse()
      }
    }

    if (props.page !== undefined && props.pageSize !== undefined) {
      data = data.slice(props.page * props.pageSize, props.page * props.pageSize + props.pageSize)
    }

    return data
  }

  // TODO: Show correct count based on applied filters
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async loadCount(props: DataQueryProps): Promise<number> {
    const data = await firstValueFrom(this.roleStore.state$.items)
    return data.length
  }

  protected config(): ListModelConfig {
    return {
      name: this.modelName,
      defaultOrderColumn: 'createdAt',
      defaultOrderDirection: 'DESC',
      gridColumns: [
        {
          field: 'name',
          label: 'roles.grid.role',
          type: DataGridColumnType.Template,
          clickable: true,
          sortable: true,
        },
        {
          field: 'usersCount',
          label: 'roles.grid.assigned-users',
          type: DataGridColumnType.Text,
          sortable: true,
        },
        {
          field: 'createdAt',
          label: 'roles.grid.created-at',
          format: 'grid-date-time',
          type: DataGridColumnType.Date,
          sortable: true,
        },
        {
          field: 'actions',
          label: '',
          type: DataGridColumnType.Actions,
          actions: ((row: RolesListData) => this.getRowActions$(row)) as DataGridColumnActionsFn,
        },
      ],
    }
  }

  protected getRowActions$(data: RolesListData): Observable<MenuItem[]> {
    return this.accessControl
      .massCheck$([
        [Resource.Role, Action.Create],
        [Resource.Role, Action.Update],
        [Resource.Role, Action.Delete],
      ])
      .pipe(
        map(([canCreate, canUpdate, canDelete]) => {
          const options: MenuItem[] = []

          const isRoleCustom = data.id >= TI_CUSTOM_ROLE_ID_RANGE[0]
          if (isRoleCustom) {
            options.push(
              {
                label: 'roles.buttons.edit',
                icon: 'pi pi-pencil',
                styleClass: `${canUpdate ? '' : 'not-allowed'}`,
                command: () => (canUpdate ? this.select(data) : undefined),
              },
              {
                label: 'roles.buttons.duplicate',
                iconComponent: CopyOutlinedIconComponent,
                styleClass: `has-bottom-border ${canCreate ? '' : 'not-allowed'}`,
                command: () => (canCreate ? this.rolesManager.duplicate(data) : undefined),
              },
              {
                label: 'roles.buttons.delete',
                icon: 'pi pi-trash',
                styleClass: `error ${canDelete && !data.usersCount ? '' : 'not-allowed'}`,
                tooltip: canDelete && data.usersCount ? 'roles.tooltips.cannot-delete' : undefined,
                command: () =>
                  canDelete && !data.usersCount ? this.rolesManager.delete(data) : undefined,
              },
            )
          } else {
            options.push(
              {
                label: 'roles.buttons.view-system',
                iconComponent: WrenchIconComponent,
                styleClass: `has-bottom-border ${canUpdate ? '' : 'not-allowed'}`,
                command: () => (canUpdate ? this.select(data) : undefined),
              },
              {
                label: 'roles.buttons.duplicate-system',
                iconComponent: CopyOutlinedIconComponent,
                styleClass: `${canUpdate ? '' : 'not-allowed'}`,
                command: () => (canUpdate ? this.rolesManager.duplicate(data) : undefined),
              },
            )
          }

          return options
        }),
        takeUntil(this.destroy$),
        shareReplay({ bufferSize: 1, refCount: false }),
      )
  }
}
